import { Badge } from "@/components/ui/badge";
import type { Sprint } from "@/services/sprint.service";
import type { UserGroup } from "./groupProdTasks";

const STATUS_VARIANTS = new Set([
	"backlog",
	"todo",
	"in-progress",
	"review",
	"done",
	"cancelled",
]);

const statusVariant = (status: string) =>
	(STATUS_VARIANTS.has(status) ? status : "default") as never;

export default function ProdReportTable({
	groups,
	sprint,
}: {
	groups: UserGroup[];
	sprint: Sprint | null;
}) {
	const title = sprint
		? `${sprint.name} (${sprint.start_date} to ${sprint.end_date})`
		: "All sprints";

	return (
		<div className="overflow-x-auto">
			<div className="bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
				{title}
			</div>
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-border bg-muted-subtle">
						<th className="w-[300px] px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wider text-muted">
							User
						</th>
						<th className="px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wider text-muted">
							Task{" "}
							<span className="normal-case">
								(Project Name / Task Title / Status)
							</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{groups.map((u) => (
						<tr
							key={u.userId ?? "none"}
							className="border-b border-border align-top last:border-0"
						>
							<td className="px-4 py-3 text-sm font-medium text-foreground">
								{u.name}
								{u.email && (
									<span className="block text-xs font-normal text-muted">
										{u.email}
									</span>
								)}
							</td>
							<td className="px-4 py-3">
								<ul className="space-y-1.5">
									{u.tasks.map((t) => (
										<li
											key={t.id}
											className="flex flex-wrap items-center gap-2"
										>
											<span className="text-xs text-muted">
												{t.projectName}
											</span>
											<span className="text-muted">
												/
											</span>
											<a
												href={`/tasks/${t.id}`}
												target="_blank"
												rel="noopener noreferrer"
												className="text-sm font-medium text-primary hover:underline"
											>
												{t.title}
											</a>
											<Badge
												variant={statusVariant(
													t.status,
												)}
											>
												{t.statusLabel}
											</Badge>
										</li>
									))}
								</ul>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
